import {
    useState, useEffect, useRef, useCallback
} from 'react';
import {
    getContextAwarePlacement, getContextAwareLabel, hasContextChangedFromEntryPoint, getNewEntryPoint
} from 'ai/utils/aiBeautyChat';
import { removeLastFollowUps } from 'ai/utils/sessionStorage';
import { ENTRYPOINTS } from 'ai/constants/superchat';

const getPromptContent = prompt => (typeof prompt === 'string' ? prompt : prompt?.content);

const useSuperChatContext = ({
    initialEntryPointData, page, contextPrompts, messagesCount, onContextChange
}) => {
    const [entryPointData, setEntryPointData] = useState(initialEntryPointData);
    const [newContextDetected, setNewContextDetected] = useState(false);
    const [contextualChipContextAware, setContextualChipContextAware] = useState(null);
    const entryPointDataRef = useRef(entryPointData);
    const lastPageRef = useRef(page);

    useEffect(() => {
        setEntryPointData(initialEntryPointData);
        entryPointDataRef.current = initialEntryPointData;
    }, [initialEntryPointData]);

    useEffect(() => {
        if (lastPageRef.current !== page) {
            setNewContextDetected(false);
            lastPageRef.current = page;
        }
    }, [page?.templateInformation?.template, page?.nthCategory?.categoryId, page?.product?.productId, page?.search?.keyword]);

    useEffect(() => {
        const currentPlacement = getContextAwarePlacement(page);
        const isNewContext = hasContextChangedFromEntryPoint(page, entryPointData);

        if (isNewContext) {
            setNewContextDetected(true);
            const label = getContextAwareLabel(page, currentPlacement);
            const newEntryPointData = getNewEntryPoint('', page);

            if (newEntryPointData) {
                setEntryPointData(newEntryPointData);
                entryPointDataRef.current = newEntryPointData;
            } else {
                // Page is not in supported AI chat categories/terms.
                // Align entryPointData with the current page placement to prevent
                // repeated false context-change detection on every message update.
                const fallbackData = currentPlacement ? { entrypoint: currentPlacement } : { entrypoint: ENTRYPOINTS.DEFAULT };
                setEntryPointData(fallbackData);
                entryPointDataRef.current = fallbackData;
            }

            if (currentPlacement) {
                removeLastFollowUps();
            }

            if (currentPlacement) {
                setContextualChipContextAware({
                    label,
                    followUps: []
                });
            } else {
                setContextualChipContextAware(null);
            }

            if (onContextChange) {
                onContextChange();
            }
        } else {
            const hasContextPrompts = contextPrompts && contextPrompts.length > 0;
            const isOnContextAwarePage = currentPlacement === 'PLP' || currentPlacement === 'PDP' || currentPlacement === 'SRP';

            if (messagesCount > 0) {
                setNewContextDetected(false);
            }

            if (hasContextPrompts && isOnContextAwarePage) {
                setContextualChipContextAware(prev => {
                    const currentFollowUps = prev?.followUps;
                    const hasMatchingContent =
                        currentFollowUps?.length === contextPrompts.length &&
                        currentFollowUps?.every((item, index) => {
                            return getPromptContent(item) === getPromptContent(contextPrompts[index]);
                        });

                    if (hasMatchingContent) {
                        return prev;
                    }

                    return {
                        label: prev?.label || getContextAwareLabel(page, currentPlacement),
                        followUps: contextPrompts
                    };
                });
            } else if (!isOnContextAwarePage) {
                setContextualChipContextAware(null);
            }
        }
    }, [
        page?.templateInformation?.template,
        page?.nthCategory?.categoryId,
        page?.product?.productId,
        page?.search?.keyword,
        contextPrompts,
        entryPointData,
        messagesCount,
        onContextChange
    ]);

    const updateEntryPointData = useCallback(newData => {
        setEntryPointData(newData);
        entryPointDataRef.current = newData;
    }, []);

    const clearSelectedPrompt = useCallback(() => {
        const cleanedData = { ...entryPointDataRef.current };
        delete cleanedData.selectedPrompt;
        setEntryPointData(cleanedData);
        entryPointDataRef.current = cleanedData;
    }, []);

    return {
        entryPointData,
        entryPointDataRef,
        contextualChipContextAware,
        updateEntryPointData,
        clearSelectedPrompt,
        newContextDetected
    };
};

export default useSuperChatContext;
